/** Home del portale: KPI delle sezioni, sezioni attive e in arrivo, «Da fare per te», novità della settimana. */
import { Card, Col, Row, Table, Tag } from "antd";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { HomeDto, SezioneDto } from "@qa/shared";
import { api } from "../api/client";
import { linguaCorrente } from "../i18n";
import { fmtK, fmtNum } from "../lib/format";
import { palette } from "../theme";
import { Flag, Kpi, KpiRow, PageHead, Sev, Stato, Urgente, Vuoto } from "../components/ui";

const COLORE_SEZIONE: Record<string, string> = { redflag: palette.crit, claim: palette.secH, audit: palette.primary, ispezioni: palette.secX };

function Tessera({ s }: { s: SezioneDto }) {
  const { t } = useTranslation();
  const corpo = (
    <>
      <div className="ti" style={{ background: COLORE_SEZIONE[s.chiave] ?? palette.primary }}>
        {s.sigla}
      </div>
      <b>{s.nome}</b>
      <small>{s.descrizione}</small>
      <span className="tb">
        <Tag bordered={false} color={s.stato === "attiva" ? "success" : "default"}>
          {s.stato === "attiva" ? t("comune.attiva") : t("comune.inArrivo")}
        </Tag>
      </span>
    </>
  );
  return s.stato === "attiva" && s.percorso ? (
    <Link className="tile" to={s.percorso}>
      {corpo}
    </Link>
  ) : (
    <div className="tile off" aria-disabled="true">
      {corpo}
    </div>
  );
}

export default function HomePage() {
  const { t } = useTranslation();
  const lingua = linguaCorrente();
  const q = useQuery({ queryKey: ["home", lingua], queryFn: () => api.get<HomeDto>("/portale/home", { lingua }) });
  return (
    <>
      <PageHead title={t("portale.titolo")} sub={t("home.sottotitolo")} />
      <Stato q={q}>
        {(h) => (
          <>
            <KpiRow>
              {h.kpi.rfAperti !== null && <Kpi label={t("home.kpiRfAperti")} value={fmtNum(h.kpi.rfAperti)} note={t("home.kpiCritici", { n: h.kpi.rfCritici ?? 0 })} />}
              {h.kpi.ncSenzaOwner !== null && (
                <Kpi label={t("home.kpiNcSenzaOwner")} value={fmtNum(h.kpi.ncSenzaOwner)} color={h.kpi.ncSenzaOwner ? palette.bad : undefined} note={t("home.kpiSuNc", { n: h.kpi.ncTotali ?? 0 })} />
              )}
              {h.kpi.valoreARischio !== null && <Kpi label={t("home.kpiValore")} sensibile value={fmtK(h.kpi.valoreARischio)} note={t("home.kpiValoreNota")} />}
              {h.kpi.claimAperti !== null && (
                <Kpi
                  sensibile
                  label={t("home.kpiClaim")}
                  value={fmtNum(h.kpi.claimAperti)}
                  note={[h.kpi.claimUsd ? `${fmtNum(h.kpi.claimUsd)} $` : null, h.kpi.claimEur ? `${fmtNum(h.kpi.claimEur)} €` : null].filter(Boolean).join(" + ") || undefined}
                />
              )}
              <Kpi label={t("home.kpiDaFare")} value={fmtNum(h.kpi.daFare)} note={t("home.kpiDaFareNota")} />
            </KpiRow>

            <h2 style={{ margin: "20px 0 10px", fontSize: 16 }}>{t("home.sezioni")}</h2>
            <div className="tiles">
              {h.sezioni.map((s) => (
                <Tessera key={s.chiave} s={s} />
              ))}
            </div>

            <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
              <Col xs={24} lg={12}>
                <Card title={t("home.daFare")} size="small">
                  {h.daFare.length ? (
                    <Table
                      size="small"
                      showHeader={false}
                      pagination={false}
                      rowKey={(r) => `${r.tipo}-${r.codice ?? r.link}`}
                      dataSource={h.daFare}
                      columns={[
                        {
                          key: "t",
                          render: (_, r) =>
                            r.codice ? (
                              <>
                                <Link to={r.link} className="mono">
                                  {r.codice}
                                </Link>{" "}
                                <span className={["nc_senza_owner", "nc_scaduta", "azione_scaduta", "pro09_ritardo"].includes(r.tipo) ? "miss" : undefined}>{r.testo}</span>
                              </>
                            ) : (
                              <Link to={r.link}>{r.testo}</Link>
                            ),
                        },
                      ]}
                    />
                  ) : (
                    <Vuoto titolo={t("home.niente")} />
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title={t("home.novita")} size="small">
                  {h.novita.length ? (
                    <Table
                      size="small"
                      showHeader={false}
                      pagination={false}
                      rowKey="id"
                      dataSource={h.novita}
                      columns={[
                        { key: "s", width: 90, render: (_, r) => <Sev s={r.severita} /> },
                        {
                          key: "c",
                          render: (_, r) => (
                            <>
                              <Link to={`/redflag/segnalazioni/${r.id}`}>{r.cliente}</Link>
                              <Flag f={r.flag} />
                              {r.urgente && <Urgente />}
                            </>
                          ),
                        },
                        { key: "v", align: "right", render: (_, r) => <span className="num" data-sensitive style={{ whiteSpace: "nowrap" }}>{r.valoreTesto ?? ""}</span> },
                      ]}
                    />
                  ) : (
                    <Vuoto titolo={t("home.nessunaNovita")} />
                  )}
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Stato>
    </>
  );
}
